// My agent's features (M4-03; 00 §4 "inbox triage, daily brief, reminders, drafting replies";
// OPEN-QUESTIONS 37), three sections of the My agent screen, each saving on its own:
// - Schedule: the daily brief (on/off, the time in the company's zone, the weekdays), "Brief me
//   now" (posts today's brief into `#brief` and links there), the heartbeat (RRULE lines, each
//   read back as a sentence; the API refuses one that does not parse with a 422 shown inline) and
//   inbox triage on/off. The whole set is one `PUT crews/me/features`;
// - Reminders: add (text and a date-time), the open ones soonest first, dismiss. They sit on a
//   seat you hold (none: the API's 422 `no_seat` says so);
// - Role memory: tick what your agent may read (`available`: your seats, their departments, the
//   leadership memory of a seat with reports). Nothing is shared unless ticked. It is read when
//   opened ("Choose role memory"), so the screen loads in crews.me and the reminders.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  Field,
  FieldDescription,
  FieldLabel,
  InfoTip,
  Input,
  Label,
  Notice,
  SaveIndicator,
  SettingsSection,
  Skeleton,
  ToggleRow,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BellIcon, BellOffIcon, BrainIcon, CalendarClockIcon } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { crewKeys, crewMemoryGrantsQuery, crewRemindersQuery } from "../queries";
import { problemText } from "../settings/problem-text";
import { RowActions } from "../settings/row-actions";
import { describeSchedule } from "../structure/rrule";
import { ReminderEditAction } from "./reminder-edit";

type CrewView = OperationOutput<"crews.me">;
type Features = CrewView["features"];
type Posted = OperationOutput<"crews.brief"> & { topic_id: string | null };
type Reminder = OperationOutput<"crews.addReminder">;
type Grants = OperationOutput<"crews.memoryGrants">;

/** ISO weekdays, Monday first (the API's `brief.days`). */
const DAYS: { day: number; short: string; long: string }[] = [
  { day: 1, short: "Mon", long: "Monday" },
  { day: 2, short: "Tue", long: "Tuesday" },
  { day: 3, short: "Wed", long: "Wednesday" },
  { day: 4, short: "Thu", long: "Thursday" },
  { day: 5, short: "Fri", long: "Friday" },
  { day: 6, short: "Sat", long: "Saturday" },
  { day: 7, short: "Sun", long: "Sunday" },
];

export type FeatureProps = { api: Api; company: CompanyAccess; view: CrewView };

function ErrorLine({ error, fallback }: { error: unknown; fallback: string }) {
  return error ? (
    <Notice tone="danger" role="alert">
      {problemText(error, fallback)}
    </Notice>
  ) : null;
}

/** The settings as the PUT body takes them (`last_on` is the API's). */
type Draft = Omit<Features, "brief"> & { brief: Omit<Features["brief"], "last_on"> };
const draftOf = (f: Features): Draft => ({
  triage_enabled: f.triage_enabled,
  brief: { enabled: f.brief.enabled, time: f.brief.time, days: [...f.brief.days] },
  heartbeat_schedule: [...f.heartbeat_schedule],
});

/**
 * COS-SLICE1: the header's one-click daily brief. Off: a visible "Switch on" (the rest of the
 * schedule kept as it is); on: an "On" badge. The Schedule section keeps its own toggle.
 */
export function BriefSwitch({ api, company, view }: FeatureProps) {
  const co = company.id;
  const qc = useQueryClient();
  const on = useMutation({
    mutationFn: () => {
      const d = draftOf(view.features);
      return api.client.crews.putFeatures({
        params: { co },
        body: { ...d, brief: { ...d.brief, enabled: true } },
      });
    },
    onSuccess: (v) => qc.setQueryData(crewKeys.me(co), v),
  });
  return (
    <span className="inline-flex items-center gap-1.5" data-slot="brief-switch">
      <span className="text-xs text-muted-foreground">Daily brief</span>
      {view.features.brief.enabled ? (
        <Badge variant="success">On</Badge>
      ) : (
        <>
          <Button
            type="button"
            variant="secondary"
            size="xs"
            disabled={on.isPending}
            onClick={() => on.mutate()}
          >
            {on.isPending ? "Switching on…" : "Switch on"}
          </Button>
          <InfoTip label="About the daily brief">
            Turns on your daily brief in #brief. Reminders work already.
          </InfoTip>
        </>
      )}
    </span>
  );
}

export function ScheduleSection({ api, company, view }: FeatureProps) {
  const co = company.id;
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => draftOf(view.features));
  // The header's "Switch on" changes the saved brief; the toggle here follows it.
  const savedOn = view.features.brief.enabled;
  useEffect(() => {
    setDraft((d) =>
      d.brief.enabled === savedOn ? d : { ...d, brief: { ...d.brief, enabled: savedOn } },
    );
  }, [savedOn]);
  const [line, setLine] = useState("");
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [brief, setBrief] = useState<Posted | null>(null);
  const timeId = useId();
  const ruleId = useId();
  const save = useMutation({
    mutationFn: (body: Draft) => api.client.crews.putFeatures({ params: { co }, body }),
    onSuccess: (v) => {
      qc.setQueryData(crewKeys.me(co), v);
      setDraft(draftOf(v.features));
      setSavedAt(Date.now());
    },
  });
  const now = useMutation({
    // The brief, and its topic (the link opens the day's topic, else `#brief` itself).
    mutationFn: async (): Promise<Posted> => {
      const b = await api.client.crews.brief({ params: { co } });
      const thread = await api.client.threads
        .get({ params: { co, id: b.thread_id } })
        .catch(() => null);
      return { ...b, topic_id: thread?.kind === "topic" ? thread.subject_id : null };
    },
    onSuccess: (b) => {
      setBrief(b);
      void qc.invalidateQueries({ queryKey: crewKeys.me(co) });
    },
  });
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(view.features));
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const setBriefPart = (patch: Partial<Draft["brief"]>) =>
    setDraft((d) => ({ ...d, brief: { ...d.brief, ...patch } }));
  const toggleDay = (day: number, on: boolean) =>
    setBriefPart({
      days: on
        ? [...new Set([...draft.brief.days, day])].sort((a, b) => a - b)
        : draft.brief.days.filter((d) => d !== day),
    });
  const addLine = () => {
    const rule = line.trim();
    if (!rule || draft.heartbeat_schedule.includes(rule)) return;
    set({ heartbeat_schedule: [...draft.heartbeat_schedule, rule] });
    setLine("");
  };
  const tz = company.calendar?.timezone ?? "UTC";
  const full = draft.heartbeat_schedule.length >= 8;
  return (
    <SettingsSection
      id="schedule"
      icon={CalendarClockIcon}
      title="Schedule"
      description="Your daily brief, when your agent wakes up on its own, and inbox triage."
    >
      <form
        aria-label="Schedule"
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(draft);
        }}
      >
        <div className="flex flex-col">
          <ToggleRow
            label="Daily brief"
            description="Each chosen day, your agent posts what needs you into #brief."
            checked={draft.brief.enabled}
            onCheckedChange={(enabled) => setBriefPart({ enabled })}
          />
          <ToggleRow
            label="Inbox triage"
            description="Your agent suggests Done, Snooze or a drafted reply on your inbox items; you accept or dismiss each in the Inbox."
            checked={draft.triage_enabled}
            onCheckedChange={(triage_enabled) => set({ triage_enabled })}
          />
        </div>
        <div className="grid grid-cols-[10rem_minmax(0,1fr)] gap-5 max-md:grid-cols-1">
          <Field>
            <FieldLabel htmlFor={timeId}>Brief time</FieldLabel>
            <Input
              id={timeId}
              type="time"
              className="h-10"
              value={draft.brief.time}
              required
              onChange={(e) => setBriefPart({ time: e.target.value })}
            />
            <FieldDescription>{tz}</FieldDescription>
          </Field>
          <fieldset className="flex flex-col gap-2" data-slot="brief-days">
            <legend className="mb-2 text-sm font-medium">Brief days</legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {DAYS.map((d) => (
                <div key={d.day} className="flex items-center gap-1.5">
                  <Checkbox
                    id={`${timeId}-day-${d.day}`}
                    checked={draft.brief.days.includes(d.day)}
                    onCheckedChange={(on) => toggleDay(d.day, on === true)}
                  />
                  <Label htmlFor={`${timeId}-day-${d.day}`} title={d.long} className="font-normal">
                    {d.short}
                  </Label>
                </div>
              ))}
            </div>
          </fieldset>
        </div>
        <div className="flex flex-col gap-2" data-slot="heartbeat">
          <span className="text-sm font-medium">Heartbeat</span>
          <p className="text-xs text-muted-foreground">
            Each line is a recurring time your agent wakes up and looks at your work (an RRULE, in{" "}
            {tz}).
          </p>
          {draft.heartbeat_schedule.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              None: your agent only wakes when you ask it.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-border-subtle">
              {draft.heartbeat_schedule.map((r) => (
                <li key={r} data-rule={r} className="flex items-center gap-3 py-2">
                  <span className="min-w-0 flex-1 text-sm">
                    {describeSchedule(r)}
                    <span className="block truncate font-mono text-xs text-muted-foreground">
                      {r}
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${r}`}
                    onClick={() =>
                      set({ heartbeat_schedule: draft.heartbeat_schedule.filter((x) => x !== r) })
                    }
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-end gap-2">
            {/* `relative` holds the sr-only label inside the scrolling main: positioned against
                the page instead, it made the document taller than the viewport (a grey band). */}
            <Field className="relative flex-1">
              <FieldLabel htmlFor={ruleId} className="sr-only">
                New heartbeat line
              </FieldLabel>
              <Input
                id={ruleId}
                className="h-10 font-mono"
                placeholder="FREQ=WEEKLY;BYDAY=MO,WE,FR;BYHOUR=9;BYMINUTE=0"
                value={line}
                maxLength={2000}
                disabled={full}
                onChange={(e) => setLine(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addLine();
                  }
                }}
              />
            </Field>
            <Button
              type="button"
              variant="secondary"
              disabled={!line.trim() || full}
              onClick={addLine}
            >
              Add line
            </Button>
          </div>
          {full ? <FieldDescription>At most 8 lines.</FieldDescription> : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={!dirty || save.isPending || !draft.brief.time}>
            {save.isPending ? "Saving…" : "Save schedule"}
          </Button>
          <SaveIndicator savedAt={savedAt} />
          <Button
            type="button"
            variant="secondary"
            className="ml-auto"
            disabled={now.isPending || view.crew.state !== "active"}
            onClick={() => now.mutate()}
          >
            {now.isPending ? "Posting…" : "Brief me now"}
          </Button>
        </div>
        <ErrorLine error={save.error} fallback="The schedule could not be saved." />
        <ErrorLine error={now.error} fallback="The brief could not be posted." />
        {brief ? (
          <Notice tone="success" data-slot="brief-posted">
            Today's brief is in{" "}
            {brief.topic_id ? (
              <Link
                to="/c/$slug/chat/$channelId/$topicId"
                params={{
                  slug: company.slug,
                  channelId: brief.channel_id,
                  topicId: brief.topic_id,
                }}
                className="font-medium underline underline-offset-2"
              >
                #brief
              </Link>
            ) : (
              <Link
                to="/c/$slug/chat/$channelId"
                params={{ slug: company.slug, channelId: brief.channel_id }}
                className="font-medium underline underline-offset-2"
              >
                #brief
              </Link>
            )}
            .
          </Notice>
        ) : view.features.brief.last_on ? (
          <p className="text-xs text-muted-foreground">
            Last brief: {view.features.brief.last_on}.
          </p>
        ) : null}
      </form>
    </SettingsSection>
  );
}

/** "2026-10-20T14:30" (a `datetime-local` value) one hour from now, rounded to the minute. */
function inAnHour(): string {
  const t = new Date(Date.now() + 3_600_000);
  t.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
}

export function RemindersSection({ api, company }: Omit<FeatureProps, "view">) {
  const co = company.id;
  const qc = useQueryClient();
  const list = useQuery(crewRemindersQuery(api, co));
  const [text, setText] = useState("");
  const [when, setWhen] = useState(inAnHour);
  const textId = useId();
  const whenId = useId();
  const refresh = () => qc.invalidateQueries({ queryKey: crewKeys.reminders(co) });
  const add = useMutation({
    mutationFn: (b: { text: string; at: string }) =>
      api.client.crews.addReminder({ params: { co }, body: b }),
    onSuccess: () => {
      setText("");
      void refresh();
    },
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => api.client.crews.dismissReminder({ params: { co, id } }),
    onSuccess: () => void refresh(),
  });
  const at = when ? new Date(when) : null;
  const valid = Boolean(text.trim()) && at !== null && !Number.isNaN(at.getTime());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid && at) add.mutate({ text: text.trim(), at: at.toISOString() });
  };
  return (
    <SettingsSection
      id="reminders"
      icon={BellIcon}
      title="Reminders"
      description="Nudges on your seat: each one lands in your Inbox when it is due."
    >
      <form aria-label="Add a reminder" className="flex flex-col gap-3" onSubmit={submit}>
        <div className="grid grid-cols-[minmax(0,1fr)_14rem_auto] items-end gap-3 max-md:grid-cols-1">
          <Field>
            <FieldLabel htmlFor={textId}>Reminder</FieldLabel>
            <Input
              id={textId}
              className="h-10"
              value={text}
              maxLength={500}
              placeholder="Call Northwind about the offer"
              onChange={(e) => setText(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={whenId}>When</FieldLabel>
            <Input
              id={whenId}
              type="datetime-local"
              className="h-10"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={!valid || add.isPending}>
            {add.isPending ? "Adding…" : "Add reminder"}
          </Button>
        </div>
        <ErrorLine error={add.error} fallback="The reminder could not be added." />
      </form>
      {list.isPending ? (
        <div role="status" aria-label="Loading your reminders" className="flex flex-col gap-2">
          <Skeleton className="h-10" />
        </div>
      ) : list.isError ? (
        <ErrorLine error={list.error} fallback="Your reminders could not be loaded." />
      ) : list.data.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No reminders.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle" data-slot="reminders">
          {list.data.data.map((r: Reminder) => (
            <li key={r.id} data-reminder={r.id} className="group/row flex items-center gap-3 py-2">
              <span className="min-w-0 flex-1 text-sm">
                {r.text}
                <span className="block text-xs text-muted-foreground">
                  {new Date(r.at).toLocaleString()}
                </span>
              </span>
              {r.fired_at ? <Badge variant="outline">Sent to your Inbox</Badge> : null}
              {r.origin === "crew" ? <Badge variant="info">From your agent</Badge> : null}
              <RowActions>
                <ReminderEditAction api={api} co={co} reminder={r} />
                <ConfirmRowAction
                  icon={BellOffIcon}
                  label={`Dismiss ${r.text}`}
                  tip="Dismiss"
                  disabled={dismiss.isPending}
                  title={`Dismiss “${r.text}”?`}
                  description={r.fired_at ? undefined : "It won't remind you."}
                  confirmLabel="Dismiss"
                  onConfirm={() => dismiss.mutate(r.id)}
                />
              </RowActions>
            </li>
          ))}
        </ul>
      )}
      <ErrorLine error={dismiss.error} fallback="The reminder could not be dismissed." />
    </SettingsSection>
  );
}

const keyOf = (g: { bucket: string; subject_id: string }) => `${g.bucket}:${g.subject_id}`;

export function MemorySection({ api, company }: Omit<FeatureProps, "view">) {
  const co = company.id;
  // Read on demand: the screen's load stays within its call budget (spec 13 acceptance 3).
  const [open, setOpen] = useState(false);
  const grants = useQuery({ ...crewMemoryGrantsQuery(api, co), enabled: open });
  return (
    <SettingsSection
      id="role-memory"
      icon={BrainIcon}
      title="Role memory"
      description="What your agent may read of the memory that comes with your role."
      actions={
        open ? null : (
          <Button variant="secondary" onClick={() => setOpen(true)}>
            Choose role memory
          </Button>
        )
      }
    >
      <p className="text-sm text-muted-foreground" data-slot="memory-explainer">
        Nothing is shared with your agent unless you tick it. Your private notes are never shared.
      </p>
      {!open ? null : grants.isPending ? (
        <div role="status" aria-label="Loading role memory" className="flex flex-col gap-2">
          <Skeleton className="h-10" />
        </div>
      ) : grants.isError ? (
        <ErrorLine error={grants.error} fallback="Role memory could not be loaded." />
      ) : (
        // One key per saved set: the ticks start from what is saved.
        <MemoryForm
          key={grants.data.data.map(keyOf).sort().join(",")}
          api={api}
          co={co}
          grants={grants.data}
        />
      )}
    </SettingsSection>
  );
}

function MemoryForm({ api, co, grants }: { api: Api; co: string; grants: Grants }) {
  const qc = useQueryClient();
  const saved = new Set(grants.data.map(keyOf));
  const [picked, setPicked] = useState<Set<string>>(saved);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const baseId = useId();
  const save = useMutation({
    mutationFn: (keys: Set<string>) =>
      api.client.crews.putMemoryGrants({
        params: { co },
        body: {
          grants: grants.available
            .filter((a) => keys.has(keyOf(a)))
            .map((a) => ({ bucket: a.bucket, subject_id: a.subject_id })),
        },
      }),
    onSuccess: (g) => {
      qc.setQueryData(crewKeys.memoryGrants(co), g);
      setSavedAt(Date.now());
    },
  });
  if (grants.available.length === 0)
    return (
      <p className="text-sm text-muted-foreground">
        You hold no seat here, so there is no role memory to grant.
      </p>
    );
  const dirty = [...picked].sort().join(",") !== [...saved].sort().join(",");
  return (
    <form
      aria-label="Role memory"
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(picked);
      }}
    >
      <div className="flex flex-col gap-2" data-slot="memory-subjects">
        {grants.available.map((a) => {
          const id = `${baseId}-${a.bucket}-${a.subject_id}`;
          return (
            <div key={keyOf(a)} data-bucket={a.bucket} className="flex items-center gap-2">
              <Checkbox
                id={id}
                aria-label={a.label}
                checked={picked.has(keyOf(a))}
                onCheckedChange={(on) =>
                  setPicked((cur) => {
                    const next = new Set(cur);
                    if (on === true) next.add(keyOf(a));
                    else next.delete(keyOf(a));
                    return next;
                  })
                }
              />
              <Label htmlFor={id} className="font-normal">
                {a.label}
              </Label>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save role memory"}
        </Button>
        <SaveIndicator savedAt={savedAt} />
      </div>
      <ErrorLine error={save.error} fallback="Role memory could not be saved." />
    </form>
  );
}
